import React, { useEffect, useRef, useState } from 'react';

/**
 * Animates a numeric value from 0 to `value` when it scrolls into view.
 * Preserves the prefix/suffix ("£", "%", "/yr" etc).
 */
export const useCountUp = (endValue, duration = 1400) => {
  const [current, setCurrent] = useState(0);
  const rafRef = useRef(null);

  useEffect(() => {
    const startTime = performance.now();
    const startVal = 0;
    const step = (now) => {
      const t = Math.min(1, (now - startTime) / duration);
      // easeOutCubic
      const eased = 1 - Math.pow(1 - t, 3);
      setCurrent(Math.round(startVal + (endValue - startVal) * eased));
      if (t < 1) rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => rafRef.current && cancelAnimationFrame(rafRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endValue]);

  return current;
};

/**
 * Parses a pretty currency/number string like "£7,200" or "₹32,000" or "C$9,852"
 * into { prefix, num, suffix } so we can animate just the numeric part.
 */
export const splitCurrencyNumber = (str) => {
  if (!str) return { prefix: '', num: 0, suffix: '' };
  const match = String(str).match(/^([^\d-]*)([\d,.-]+)(.*)$/);
  if (!match) return { prefix: '', num: 0, suffix: String(str) };
  const [, prefix, numStr, suffix] = match;
  const num = parseFloat(numStr.replace(/,/g, '')) || 0;
  return { prefix: prefix || '', num, suffix: suffix || '' };
};

export const AnimatedNumber = ({ value, className = '', duration = 1400 }) => {
  const { prefix, num, suffix } = splitCurrencyNumber(value);
  // If the input has no numeric part, render it as-is (prevents "0Unlimited" artefacts).
  const hasNumber = /\d/.test(String(value || ''));
  const animated = useCountUp(hasNumber ? num : 0, duration);
  if (!hasNumber) {
    return <span className={className}>{value}</span>;
  }
  const formatted = animated.toLocaleString();
  return (
    <span className={className}>
      {prefix}{formatted}{suffix}
    </span>
  );
};
